import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminCrmService from '../../../services/adminCrmService';
import '../../../assets/styles/PersonalIDs.css';
import '../../assets/styles/CRMOpenLead.css';
import arrowleftt from '../../assets/img/arrowleftt.svg';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function AdminCrmBillingSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole, userEmail, userPhone } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (userId) fetchData();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await adminCrmService.getUserBillingSummary(userId);
      if (res.success) setData(res.data);
    } catch (err) {
      console.error('Fetch billing summary error:', err);
    } finally {
      setLoading(false);
    }
  };

  const subscription = data?.subscription;
  const invoices = data?.invoices || [];

  return (
    <div className="d-flex" style={{ background: '#fff', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />

        <div className="p-3">

          {/* Back + Title */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
            <h6 className="accordion-title mb-0">Billing Summary</h6>
          </div>

          {/* User name */}
          {userName && (
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '12px' }}>{userName}</p>
          )}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
            </div>
          ) : (
            <>
              {/* Current Subscription Card */}
              <div className="accordion-item mb-3">
                <div className="accordion-header d-flex justify-content-between align-items-center" style={{ cursor: 'default' }}>
                  <h6 className="accordion-title mb-0">Current Subscription</h6>
                </div>
                <div className="accordion-body">
                  <div className="card p-4 personal-ids-card">
                    {subscription ? (
                      <>
                        {[
                          { label: 'Plan', value: subscription.plan_name || 'N/A' },
                          { label: 'Price', value: formatCurrency(subscription.offer_price || subscription.price) },
                          { label: 'Start Date', value: formatDate(subscription.current_period_start) },
                          { label: 'End Date', value: formatDate(subscription.current_period_end) },
                          { label: 'Duration', value: `${subscription.duration_days || 'N/A'} days` },
                        ].map(({ label, value }) => (
                          <div key={label} className="d-flex justify-content-between align-items-center py-2"
                            style={{ borderBottom: '1px solid #F3F4F6' }}>
                            <span className="personal-label">{label}</span>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{value}</span>
                          </div>
                        ))}
                        {/* Status row */}
                        <div className="d-flex justify-content-between align-items-center py-2">
                          <span className="personal-label">Status</span>
                          <span style={{
                            backgroundColor: subscription.status === 'active' ? '#D1FAE5' : '#FEE2E2',
                            color: subscription.status === 'active' ? '#059669' : '#DC2626',
                            padding: '3px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize'
                          }}>
                            {subscription.status}
                          </span>
                        </div>
                      </>
                    ) : (
                      <p className="text-center py-3" style={{ color: '#9CA3AF', fontSize: '14px' }}>No active subscription</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="d-flex gap-3 mb-3">
                <div className="flex-grow-1 p-3 text-center" style={{ backgroundColor: '#EFF6FF', borderRadius: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#2563EB' }}>{formatCurrency(data?.total_spent)}</div>
                  <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: '500', marginTop: '4px' }}>Total Spent</div>
                </div>
                <div className="flex-grow-1 p-3 text-center" style={{ backgroundColor: '#F0FDF4', borderRadius: '12px' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#059669' }}>{data?.invoice_count || 0}</div>
                  <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: '500', marginTop: '4px' }}>Invoices</div>
                </div>
                {data?.credits_balance && (
                  <div className="flex-grow-1 p-3 text-center" style={{ backgroundColor: '#FFF7ED', borderRadius: '12px' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#D97706' }}>{data.credits_balance.balance || 0}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: '500', marginTop: '4px' }}>Credits</div>
                  </div>
                )}
              </div>

              {/* Invoices Card */}
              <div className="accordion-item mb-3">
                <div className="accordion-header d-flex justify-content-between align-items-center" style={{ cursor: 'default' }}>
                  <h6 className="accordion-title mb-0">Invoices ({invoices.length})</h6>
                </div>
                <div className="accordion-body">
                  <div className="card personal-ids-card" style={{ padding: 0, overflow: 'hidden' }}>
                    {invoices.length === 0 ? (
                      <p className="text-center py-3 px-4" style={{ color: '#9CA3AF', fontSize: '14px' }}>No invoices found</p>
                    ) : (
                      invoices.map((invoice, index) => (
                        <div key={invoice.id || index}
                          className="d-flex justify-content-between align-items-center px-4 py-3"
                          style={{ borderBottom: index < invoices.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                        >
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                              {invoice.invoice_number || `INV-${index + 1}`}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                              {invoice.plan_name || 'Subscription'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                              {formatDate(invoice.created_at)}
                            </div>
                          </div>
                          <div className="text-end">
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                              {formatCurrency(invoice.total_amount)}
                            </div>
                            <span style={{
                              backgroundColor:
                                invoice.payment_status === 'paid' ? '#D1FAE5' :
                                invoice.payment_status === 'free' ? '#EFF6FF' : '#FEF3C7',
                              color:
                                invoice.payment_status === 'paid' ? '#059669' :
                                invoice.payment_status === 'free' ? '#2563EB' : '#D97706',
                              padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
                              textTransform: 'capitalize', display: 'inline-block', marginTop: '4px'
                            }}>
                              {invoice.payment_status}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
